import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ArrowUpRight,
  Mail,
  Copy,
  Check,
  ShieldCheck,
  Heart,
} from "lucide-react";

// To change later, just edit these two lines.
const PAYID = "sophiebial@icloud.com";
const PAYID_TYPE = "Email";

const STEPS = [
  "Open your banking app",
  "Choose Pay someone new → PayID",
  "Paste the email above",
  "Enter any amount, send",
];

export default function Support() {
  const [copied, setCopied] = useState(false);

  const copyPayID = async () => {
    try {
      await navigator.clipboard.writeText(PAYID);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div data-testid="page-support" className="py-16 lg:py-24">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
          <span className="w-6 h-px bg-terracotta" />
          Support this site
        </span>

        <h1
          data-testid="support-title"
          className="font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight"
        >
          If this site has helped, you can help <em className="text-terracotta">keep it running</em>.
        </h1>

        <p className="mt-6 text-lg text-charcoal-light dark:text-bone/70 leading-relaxed max-w-2xl">
          I run Quiet Signals on my own, and I have FND myself. Donations go
          straight to hosting the site and the domain name — nothing else.
        </p>

        {/* PayID panel */}
        <section
          data-testid="payid-panel"
          className="mt-14 rounded-[2rem] border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 p-8 lg:p-12"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-5">
                <Heart size={14} strokeWidth={1.5} />
                Direct bank transfer · PayID
              </span>
              <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl leading-[1.1] tracking-tight">
                Free, instant, no third party.
              </h2>
              <p className="mt-5 text-base text-charcoal-light dark:text-bone/70 leading-relaxed">
                If you’re in Australia, you can send any amount straight from your
                banking app using the PayID below. There are no fees and no platforms
                in between — what you send is what arrives.
              </p>

              <div className="mt-8 rounded-3xl border border-bone-200 dark:border-charcoal-light/30 bg-bone-100/60 dark:bg-charcoal-light/20 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-terracotta/10 text-terracotta shrink-0">
                  <Mail size={18} strokeWidth={1.5} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/55 mb-1">
                    PayID ({PAYID_TYPE})
                  </p>
                  <p
                    data-testid="payid-value"
                    className="font-serif text-xl sm:text-2xl text-charcoal dark:text-bone tracking-tight break-all select-all"
                  >
                    {PAYID}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={copyPayID}
                  data-testid="payid-copy"
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium tracking-wide transition shrink-0 ${
                    copied
                      ? "bg-moss text-bone"
                      : "bg-terracotta text-bone hover:bg-terracotta-hover"
                  }`}
                >
                  {copied ? (
                    <>
                      <Check size={15} strokeWidth={1.5} />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={15} strokeWidth={1.5} />
                      Copy PayID
                    </>
                  )}
                </button>
              </div>

              <p className="mt-4 text-xs text-charcoal-light/80 dark:text-bone/50 leading-relaxed">
                Tip: your bank will show the recipient’s name before you confirm —
                that’s expected, and it lets you double-check you’ve got the right
                PayID before any money moves.
              </p>
            </div>

            {/* Steps */}
            <aside className="lg:col-span-5 rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone-100/40 dark:bg-charcoal-light/10 p-6 lg:p-8">
              <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-5">
                How it works
              </p>
              <ol className="space-y-4">
                {STEPS.map((s, i) => (
                  <li
                    key={s}
                    data-testid={`payid-step-${i + 1}`}
                    className="flex items-start gap-3"
                  >
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-terracotta/10 text-terracotta font-serif text-sm shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-sm text-charcoal dark:text-bone leading-snug pt-1">
                      {s}
                    </span>
                  </li>
                ))}
              </ol>
              <p className="mt-6 inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/55">
                <ShieldCheck size={12} strokeWidth={1.5} className="text-moss" />
                Australian bank accounts only
              </p>
            </aside>
          </div>
        </section>

        {/* Other ways to help */}
        <section className="mt-20" data-testid="support-meanwhile">
          <h3 className="font-serif text-2xl sm:text-3xl text-charcoal dark:text-bone tracking-tight">
            Not in Australia? <em className="text-terracotta">These help too.</em>
          </h3>
          <ul className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["Share the site.", "Send it to someone who's just been diagnosed, or to a friend who knows someone with FND."],
              ["Talk about it.", "Mention FND when it comes up. The more people know it exists, the less invisible it is."],
              ["Take what you needed.", "If you came here because you needed something — that's enough. Rest. Come back when you can."],
            ].map(([title, body]) => (
              <li
                key={title}
                className="rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone-100/40 dark:bg-charcoal-light/10 p-5 lg:p-6"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-terracotta/10 text-terracotta mb-3">
                  <Sparkles size={16} strokeWidth={1.5} />
                </span>
                <p className="font-serif text-lg text-charcoal dark:text-bone tracking-tight">
                  {title}
                </p>
                <p className="text-sm text-charcoal-light dark:text-bone/65 leading-relaxed mt-1">
                  {body}
                </p>
              </li>
            ))}
          </ul>

          <Link
            to="/resources"
            data-testid="support-cta-resources"
            className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-charcoal dark:text-bone border-b border-charcoal/30 dark:border-bone/30 pb-1 hover:text-terracotta hover:border-terracotta transition"
          >
            Looking for free resources? They’re all here.
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
        </section>

        <p className="mt-20 text-center font-serif italic text-2xl text-charcoal dark:text-bone leading-snug">
          “Whether you give nothing or everything — thank you for being here at all.”
        </p>
      </div>
    </div>
  );
}
